import { useState, useCallback } from "react";

export interface DirtyTrackingCallbacks {
  [key: number]: () => void;
}

/**
 * Hook to track dirty state and save/reset callbacks for multiple tabs
 */
export const useDirtyTracking = (tabCount: number) => {
  const [isDirty, setIsDirty] = useState<{ [key: number]: boolean }>(
    Array.from({ length: tabCount }, (_, i) => [i, false] as const).reduce(
      (acc, [key, val]: readonly [number, boolean]) => ({ ...acc, [key]: val }),
      {} as { [key: number]: boolean }
    )
  );

  const [saveDraftCallbacks, setSaveDraftCallbacks] =
    useState<DirtyTrackingCallbacks>({});
  const [resetFormCallbacks, setResetFormCallbacks] =
    useState<DirtyTrackingCallbacks>({});

  const handleDirtyChange = useCallback((tabIndex: number, dirty: boolean) => {
    setIsDirty((prev) => ({ ...prev, [tabIndex]: dirty }));
  }, []);

  const handleSaveDraftCallback = useCallback(
    (tabIndex: number, callback: () => void) => {
      setSaveDraftCallbacks((prev) => ({ ...prev, [tabIndex]: callback }));
    },
    []
  );

  const handleResetFormCallback = useCallback(
    (tabIndex: number, callback: () => void) => {
      setResetFormCallbacks((prev) => ({ ...prev, [tabIndex]: callback }));
    },
    []
  );

  const executeSaveDraft = useCallback(
    (tabIndex: number) => {
      saveDraftCallbacks[tabIndex]?.();
      setIsDirty((prev) => ({ ...prev, [tabIndex]: false }));
    },
    [saveDraftCallbacks]
  );

  const executeResetForm = useCallback(
    (tabIndex: number) => {
      resetFormCallbacks[tabIndex]?.();
      setIsDirty((prev) => ({ ...prev, [tabIndex]: false }));
    },
    [resetFormCallbacks]
  );

  const hasAnyUnsavedChanges = useCallback(() => {
    return Object.values(isDirty).some((dirty) => dirty);
  }, [isDirty]);

  return {
    isDirty,
    handleDirtyChange,
    handleSaveDraftCallback,
    handleResetFormCallback,
    executeSaveDraft,
    executeResetForm,
    hasAnyUnsavedChanges,
  };
};
